import type { PreviousApplicationsModalProps, PreviousApplicationRow } from '../../types/modals'
import { useEffect, useState } from 'react'
import { ApplicationModalLoadingState } from '../ApplicationListLoadingState'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { getPreviousApplicationList } from '../../services/applicationService'
import { showError } from '@/lib/utils/notifications'
import {
  formatApplicantId,
  formatApplicationId,
  formatDateTime,
} from '../../utils/applicationFormatters'


const tid = 'application-modal-previous-applications'


/** Angular `previous-application.component.ts` `allStatus` map. */
const STATUS_MAP: Record<string, string> = {
  INCOMPLETE: 'Incomplete',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  PROPERTY_APPROVED: 'Property Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  VOID: 'Void',
  AWAITING: 'Awaiting',
  CANCELLED: 'Cancelled',
  NOT_STARTED: 'Not Started',
  UNDERWRITING_WAITING_EXTERNAL: 'Suspend',
  UNDERWRITING_IN_PROGRESS: 'Continue',
  REFUND_PROPERTY: 'Refund Property',
  REFUND_APPLICANT: 'Refund Applicant',
  CHARGEBACK_PAYMENT_NOT_AUTHORIZED: 'Chargeback - Payment Not Authorized',
  CHARGEBACK_BANK_HOLD: 'Chargeback - Bank Hold',
  CHARGEBACK_NON_LEASED: 'Chargeback Nonlease',
  UNDERWRITING_STARTED: 'Screening Started',
}

function firstValue(row: PreviousApplicationRow, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key]
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value
    }
  }
  return undefined
}

/** Angular `previous-application.component.html` uses `progress_status_date`. */
export function resolvePreviousApplicationDate(
  row: PreviousApplicationRow,
): unknown {
  return firstValue(row, [
    'progress_status_date',
    'application_date',
    'created_at',
    'created_date',
  ])
}

/**
 * Angular uses `applicant_status` only (not `application_status`).
 * Missing / empty → "Not Started".
 */
export function resolvePreviousApplicantStatus(
  row: PreviousApplicationRow,
): string {
  const raw = firstValue(row, ['applicant_status'])
  if (raw == null || String(raw).trim() === '') return 'Not Started'
  const key = String(raw).trim().toUpperCase()
  return STATUS_MAP[key] || STATUS_MAP[String(raw).trim()] || String(raw)
}

function formatIdValue(value: unknown, formatter: (value: number) => string): string {
  if (value === undefined || value === null || String(value).trim() === '') return '-'
  const numeric = Number(value)
  return Number.isFinite(numeric) ? formatter(numeric) : String(value)
}

function formatApplicationDate(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === '') return '-'
  return formatDateTime(value as string | number | Date) || String(value)
}

function propertyName(row: PreviousApplicationRow): string {
  const nestedProperty = row.property as { property_name?: unknown } | undefined
  const nestedName = nestedProperty?.property_name
  const value = firstValue(row, ['property_name', 'propertyName', 'pmc_property_name']) ?? nestedName
  return value == null || String(value).trim() === '' ? '-' : String(value)
}

export function PreviousApplicationsModal({
  isOpen,
  onClose,
  applicantId,
}: PreviousApplicationsModalProps) {
  const [items, setItems] = useState<PreviousApplicationRow[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const res = await getPreviousApplicationList(applicantId)
        const data = res.data
        if (!cancelled) {
          setItems(
            Array.isArray(data)
              ? data.filter(
                  (item): item is PreviousApplicationRow =>
                    item !== null && typeof item === 'object',
                )
              : [],
          )
        }
      } catch (e: unknown) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Failed to load previous applications.')
          setItems([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, applicantId])

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Previous Applications"
      onClose={onClose}
      maxWidth="max-w-3xl"
      bodyScrollOnly
      overlayDataTestId={tid}
      panelDataTestId={`${tid}-panel`}
      titleDataTestId={`${tid}-title`}
      closeButtonDataTestId={`${tid}-close-btn`}
    >
      {loading ? (
        <ApplicationModalLoadingState testId={`${tid}-loading`} />
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-600" data-testid={`${tid}-empty`}>No previous applications.</p>
      ) : (
        <div className="overflow-x-auto" data-testid={`${tid}-table-wrap`}>
          <table className="min-w-full divide-y divide-slate-200 text-sm" data-testid={`${tid}-table`}>
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600">Application#</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Applicant#</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Property Name</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Application Date</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Applicant Status</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {items
                .filter((row) => {
                  // Angular: hide the current open applicant from the previous-apps list
                  const rowApplicantId = firstValue(row, [
                    'applicant_id',
                    'fk_applicant_id',
                    'applicant_number',
                  ])
                  if (rowApplicantId == null) return true
                  return String(rowApplicantId) !== String(applicantId)
                })
                .map((row, i) => {
                const applicationId = firstValue(row, [
                  'application_number',
                  'application_no',
                  'application_id',
                  'fk_application_id',
                ])
                const previousApplicantId = firstValue(row, [
                  'applicant_id',
                  'fk_applicant_id',
                  'applicant_number',
                ])
                const date = resolvePreviousApplicationDate(row)
                const statusLabel = resolvePreviousApplicantStatus(row)
                const applicantIdText = formatIdValue(previousApplicantId, formatApplicantId)

                return (
                  <tr key={`${String(previousApplicantId ?? i)}-${i}`} data-testid={`${tid}-row-${i}`}>
                    <td className="px-3 py-2 text-slate-700">
                      {formatIdValue(applicationId, formatApplicationId)}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{applicantIdText}</td>
                    <td className="px-3 py-2 text-slate-700">{propertyName(row)}</td>
                    <td className="px-3 py-2 text-slate-700">{formatApplicationDate(date)}</td>
                    <td className="px-3 py-2 text-slate-700">{statusLabel}</td>
                    <td className="px-3 py-2">
                      {previousApplicantId ? (
                        <button
                          type="button"
                          className="inline-flex rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                          data-testid={`${tid}-row-${i}-view-link`}
                          onClick={() => {
                            window.open(
                              `/application/${String(previousApplicantId)}`,
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }}
                        >
                          View
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </OnScreenModal>
  )
}
